import { useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { Notification } from '@buildmate/core';
import {
  markAllRead,
  markNotificationRead,
  subscribeToNotifications,
  systemClock,
  unreadCount,
} from '@buildmate/core';
import { Button } from '@buildmate/ui-web';
import { usePorts } from '../lib/ports-context.tsx';
import { useSession } from '../lib/session-context.tsx';

/**
 * How long ago, in the words somebody would use.
 *
 * Relative rather than a timestamp: "2h ago" is what a feed is read for,
 * and the exact instant is in the record for anyone who needs it. Falls back
 * to a date past a week, because "9 days ago" is less useful than the day.
 */
function ago(at: string, now: Date): string {
  const minutes = Math.floor((now.getTime() - new Date(at).getTime()) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return at.slice(0, 10);
}

/**
 * Where a notification takes you.
 *
 * Built from `recordRef` rather than a stored URL, so a route change does not
 * strand every notification already sent. A kind with nowhere to go yet
 * returns null and the row simply does not navigate — better than a link to
 * a page that does not exist.
 */
function routeFor(item: Notification): { to: string; params: Record<string, string> } | null {
  const { kind, jobId } = item.recordRef;
  if (!jobId) return null;
  if (kind === 'scheduleItem') {
    return { to: '/app/jobs/$jobId/schedule', params: { jobId } };
  }
  if (kind === 'job') return { to: '/app/jobs/$jobId', params: { jobId } };
  // The list rather than the bill, because there is no route to one: the
  // modal opens from the list. A sub's invoice is also the one notification
  // where the list is the better destination — somebody told an invoice
  // arrived is about to look at what else is waiting.
  if (kind === 'bill') return { to: '/app/jobs/$jobId/bills', params: { jobId } };
  return null;
}

export function NotificationBell() {
  const ports = usePorts();
  const { membership, user } = useSession();
  const navigate = useNavigate();
  const [items, setItems] = useState<readonly Notification[]>([]);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const panel = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    return subscribeToNotifications(
      { data: ports.data, companyId: membership.companyId, uid: user.uid },
      setItems,
      // Logged rather than shown in the panel: a bell that cannot load is
      // worth knowing about, but it is not what the person came to the page
      // to do, so it does not get a banner over the top of their work.
      (cause) => {
        console.error('[buildmate] notifications failed to load', cause);
        setError('Could not load your notifications.');
      },
    );
  }, [ports, membership.companyId, user.uid]);

  // Clicking away closes it, which is what every other panel in every other
  // product does. Escape too, since the panel takes focus.
  useEffect(() => {
    if (!open) return;
    const away = (event: PointerEvent) => {
      if (!panel.current?.contains(event.target as Node)) setOpen(false);
    };
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('pointerdown', away);
    window.addEventListener('keydown', key);
    return () => {
      window.removeEventListener('pointerdown', away);
      window.removeEventListener('keydown', key);
    };
  }, [open]);

  const unread = unreadCount(items);
  const context = { data: ports.data, companyId: membership.companyId, uid: user.uid };
  const now = systemClock.now();

  async function openItem(item: Notification) {
    setOpen(false);
    // Marked read on opening rather than on seeing the list: a glance at a
    // count is not having read it, and clearing on render would mean nobody
    // could leave something to come back to.
    await markNotificationRead({ ...context, clock: systemClock }, item.id);
    const route = routeFor(item);
    if (route) await navigate({ to: route.to, params: route.params });
  }

  return (
    <div className="relative" ref={panel}>
      <Button
        variant="ghost"
        size="sm"
        className="text-slate-300 hover:bg-slate-800"
        aria-expanded={open}
        // The count is in the name, not only in the badge: "Notifications, 3
        // unread" is the whole message, and a badge is a colour to a screen
        // reader.
        aria-label={unread === 0 ? 'Notifications, none unread' : `Notifications, ${unread} unread`}
        onClick={() => setOpen((current) => !current)}
      >
        <span aria-hidden="true">🔔</span>
        {unread > 0 && (
          <span
            aria-hidden="true"
            className="ml-1 rounded-full bg-red-500 px-1.5 text-xs font-semibold text-white"
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </Button>

      {open && (
        <div
          role="dialog"
          aria-label="Notifications"
          className="absolute right-0 z-50 mt-2 max-h-[28rem] w-80 overflow-y-auto rounded-lg bg-white text-slate-900 shadow-xl ring-1 ring-slate-200"
        >
          <div className="flex items-center justify-between border-b border-slate-200 p-3">
            <h2 className="text-sm font-semibold">Notifications</h2>
            {unread > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => void markAllRead({ ...context, clock: systemClock }, items)}
              >
                Mark all read
              </Button>
            )}
          </div>

          {error && (
            <p role="alert" className="p-3 text-sm text-red-600">
              {error}
            </p>
          )}

          {items.length === 0 && !error && (
            <p className="p-4 text-sm text-slate-500">
              Nothing yet. You will hear about dates you are asked to confirm, and about work
              starting soon.
            </p>
          )}

          <ul>
            {items.map((item) => (
              <li key={item.id} className="border-b border-slate-100 last:border-0">
                <button
                  type="button"
                  onClick={() => void openItem(item)}
                  className={[
                    'flex w-full flex-col gap-0.5 p-3 text-left hover:bg-slate-50',
                    item.readAt === undefined ? 'bg-blue-50/60' : '',
                  ].join(' ')}
                >
                  <span className="flex items-baseline gap-2">
                    {item.readAt === undefined && (
                      <>
                        <span
                          aria-hidden="true"
                          className="size-2 shrink-0 rounded-full bg-blue-600"
                        />
                        {/* Said, not only coloured. A dot and a tint convey
                            "unread" to people who can see them and to
                            nobody else. */}
                        <span className="sr-only">Unread.</span>
                      </>
                    )}
                    <span className="text-sm font-medium">{item.title}</span>
                  </span>
                  {item.body && <span className="text-xs text-slate-600">{item.body}</span>}
                  <span className="text-xs text-slate-500">
                    {item.actorName ? `${item.actorName} · ` : ''}
                    {ago(item.at, now)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
